// src/platform/OrgEntitlementsPanel.jsx
//
// Per-org entitlement management. Shows:
//   1. Per-SKU active pool — sq_mi_purchased vs consumed
//   2. Grant Entitlement button → modal
//   3. Entitlement history with status, term, AOI scope
//
// Edits to existing entitlements (sq_mi adjustment, term extension,
// status change) happen via inline edit modal.
import { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";

export default function OrgEntitlementsPanel({ orgId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showGrant, setShowGrant] = useState(false);
    const [editEnt, setEditEnt] = useState(null);

    async function load() {
        if (!data) setLoading(true);
        setError(null);
        try {
            const res = await fetchWithAuth(`/api/v1/platform/orgs/${orgId}/entitlements`);
            if (!res.ok) throw new Error("Failed to load entitlements");
            setData(await res.json());
        } catch (e) {
            console.error(e);
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, [orgId]);

    if (loading) {
        return (
            <div className="flex justify-center pt-6">
                <Loader2 className="animate-spin text-gray-400" size={20} />
            </div>
        );
    }

    if (error) return <div className="text-xs text-red-400">{error}</div>;
    if (!data) return null;

    const { sku_summary = [], entitlements = [] } = data;

    return (
        <div className="space-y-4">
            {/* SKU POOL SUMMARY */}
            <div className="rounded border border-bg-border bg-bg p-3 space-y-3">
                <div className="text-xs text-gray-300 font-semibold">SKU Pools</div>

                {sku_summary.length === 0 ? (
                    <div className="text-xs text-fg-secondary italic">
                        No active entitlements.
                    </div>
                ) : (
                    sku_summary.map(s => {
                        const consumed = Number(s.sq_mi_consumed) || 0;
                        const total = Number(s.sq_mi_purchased_total) || 0;
                        const pct = total > 0 ? (consumed / total) * 100 : 0;
                        const available = Math.max(0, total - consumed);

                        return (
                            <div key={s.sku_code}>
                                <div className="flex justify-between items-center text-xs">
                                    <span className="text-gray-400">
                                        {s.display_name}
                                        <span className="text-fg-secondary ml-2">
                                            ({s.entitlement_count} grant{s.entitlement_count !== 1 ? "s" : ""})
                                        </span>
                                    </span>
                                    <span className="text-gray-100">
                                        {consumed.toFixed(1)} / {total.toFixed(1)} sq mi{" "}
                                        <span className="text-fg-secondary font-normal">
                                            ({available.toFixed(1)} available)
                                        </span>
                                    </span>
                                </div>
                                <div className="mt-1 h-1.5 rounded bg-bg-soft">
                                    <div
                                        className={`h-1.5 rounded transition-[width] duration-200 ${pct >= 100 ? "bg-red-500"
                                            : pct >= 80 ? "bg-yellow-500"
                                                : "bg-status-ok"
                                            }`}
                                        style={{ width: `${Math.min(pct, 100)}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* GRANT BUTTON */}
            <div>
                <button
                    onClick={() => setShowGrant(true)}
                    className="
            flex items-center gap-2 px-3 py-1.5 rounded
            bg-accent-action/20 border border-accent-action/60
            text-accent-action hover:bg-accent-action/30 transition text-xs
          "
                >
                    <Plus size={14} />
                    Grant Entitlement
                </button>
            </div>

            {/* ENTITLEMENTS TABLE */}
            <div>
                <div className="text-xs text-gray-300 font-semibold mb-2">
                    Entitlements
                </div>

                {entitlements.length === 0 ? (
                    <div className="text-xs text-fg-secondary italic">
                        No entitlements granted.
                    </div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="text-gray-400">
                            <tr>
                                <th className="text-left py-1">SKU</th>
                                <th className="text-left py-1">Dataset</th>
                                <th className="text-left py-1">Scope</th>
                                <th className="text-left py-1">Allocated</th>
                                <th className="text-left py-1">Consumed</th>
                                <th className="text-left py-1">Term</th>
                                <th className="text-left py-1">Contract</th>
                                <th className="text-left py-1">Status</th>
                                <th className="text-left py-1"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {entitlements.map(e => {
                                const termStart = new Date(e.term_start).toLocaleDateString();
                                const termEnd = e.term_end
                                    ? new Date(e.term_end).toLocaleDateString()
                                    : "—";
                                const datasetLabel = `${e.dataset_basin}/${e.dataset_region} ${e.dataset_acquisition_date}`;
                                const scopeLabel = e.scope === "aoi"
                                    ? `AOI: ${e.coverage_aoi_name || "—"}`
                                    : "full";

                                // A row can be status='active' and STILL NOT SERVE: access
                                // control reads valid_from/valid_to, not status. is_in_effect
                                // is the server evaluating the same predicate the SKU pool
                                // summary uses, so the list and the summary stop disagreeing
                                // about how many grants an org holds.
                                //
                                // The row is not hidden or recoloured green-to-red. It keeps
                                // its own status and gains a second badge, because "revoked"
                                // and "active but the operational window is closed" are
                                // different situations and the fix for each is different.
                                const notInEffect = e.status === "active" && e.is_in_effect === false;

                                const statusColor = {
                                    active: "border-green-500/40 text-green-400",
                                    suspended: "border-yellow-500/40 text-yellow-400",
                                    revoked: "border-red-500/40 text-red-400",
                                    expired: "border-gray-500/40 text-gray-400",
                                }[e.status] || "border-gray-500/40 text-gray-400";

                                return (
                                    <tr key={e.id} className="border-t border-bg-border">
                                        <td className="py-1.5">{e.sku_display_name}</td>
                                        <td className="py-1.5 text-gray-300">{datasetLabel}</td>
                                        <td className="py-1.5 text-gray-400">{scopeLabel}</td>
                                        <td className="py-1.5">
                                            {e.scope === "aoi" && e.coverage_aoi_area_sqmi != null
                                                ? `${Number(e.coverage_aoi_area_sqmi).toFixed(1)} / ${Number(e.sq_mi_purchased).toFixed(1)}`
                                                : `— / ${Number(e.sq_mi_purchased).toFixed(1)}`}
                                        </td>
                                        <td className="py-1.5 text-gray-300">
                                            {Number(e.sq_mi_consumed).toFixed(1)}
                                        </td>
                                        <td className="py-1.5 text-gray-400">
                                            {termStart} – {termEnd}
                                        </td>
                                        <td className="py-1.5 text-gray-400">
                                            {e.contract_ref || "—"}
                                        </td>
                                        <td className="py-1.5">
                                            <span className={`px-2 py-0.5 rounded border ${notInEffect ? "border-amber-500/40 text-amber-400" : statusColor}`}>
                                                {e.status}
                                            </span>
                                            {notInEffect && (
                                                <span
                                                    className="ml-1 px-2 py-0.5 rounded border border-amber-500/40 text-amber-400"
                                                    title="Marked active, but its operational window is closed (valid_to is in the past), so it grants no access. Re-save it as active to reopen the window."
                                                >
                                                    not in effect
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-1.5 text-right">
                                            <button
                                                onClick={() => setEditEnt(e)}
                                                className="text-gray-400 hover:text-gray-200"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* GRANT MODAL */}
            {showGrant && (
                <GrantEntitlementModal
                    orgId={orgId}
                    onClose={() => setShowGrant(false)}
                    onSuccess={async () => {
                        setShowGrant(false);
                        await load();
                    }}
                />
            )}

            {/* EDIT MODAL */}
            {editEnt && (
                <EditEntitlementModal
                    entitlement={editEnt}
                    onClose={() => setEditEnt(null)}
                    onSuccess={async () => {
                        setEditEnt(null);
                        await load();
                    }}
                />
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────
// GrantEntitlementModal — full SKU-aware grant flow
// ──────────────────────────────────────────────────────────
function GrantEntitlementModal({ orgId, onClose, onSuccess }) {
    const today = new Date().toISOString().slice(0, 10);
    const [skuCode, setSkuCode] = useState("");
    const [datasetId, setDatasetId] = useState("");
    const [scope, setScope] = useState("full");
    const [coverageAoiId, setCoverageAoiId] = useState("");
    const [sqMi, setSqMi] = useState("100");
    const [termStart, setTermStart] = useState(today);
    const [termEnd, setTermEnd] = useState("");
    const [contractRef, setContractRef] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Load reference data
    const [skus, setSkus] = useState([]);
    const [datasets, setDatasets] = useState([]);
    const [aois, setAois] = useState([]);
    const [loadingRefs, setLoadingRefs] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [skuRes, dsRes, aoiRes] = await Promise.all([
                    fetchWithAuth(`/api/v1/platform/imagery-skus`),
                    fetchWithAuth(`/api/v1/platform/orgs/${orgId}/available-datasets`),
                    fetchWithAuth(`/api/v1/platform/coverage-aois`),
                ]);
                const skuData = await skuRes.json();
                const dsData = await dsRes.json();
                const aoiData = await aoiRes.json();
                setSkus(skuData.skus || []);
                setDatasets(dsData.datasets || []);
                setAois(aoiData.coverage_aois || []);
                if (skuData.skus?.length) setSkuCode(skuData.skus[0].sku_code);
            } catch (e) {
                console.error(e);
                setError("Failed to load reference data");
            } finally {
                setLoadingRefs(false);
            }
        })();
    }, [orgId]);

    async function submit() {
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetchWithAuth(
                `/api/v1/platform/orgs/${orgId}/entitlements`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        sku_code: skuCode,
                        dataset_id: datasetId,
                        scope,
                        coverage_aoi_id: scope === "aoi" ? coverageAoiId : null,
                        sq_mi_purchased: Number(sqMi),
                        term_start: termStart,
                        term_end: termEnd || null,
                        contract_ref: contractRef || null,
                    }),
                }
            );

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err?.error || "Grant failed");
            }
            await onSuccess();
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
            <div className="w-[480px] bg-bg-soft border border-bg-border rounded p-4 space-y-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-sm font-semibold">Grant Entitlement</h2>

                {loadingRefs ? (
                    <div className="text-xs text-gray-400">Loading…</div>
                ) : (
                    <>
                        <div>
                            <label className="block text-xs mb-1 text-gray-400">SKU</label>
                            <select
                                value={skuCode}
                                onChange={e => setSkuCode(e.target.value)}
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                            >
                                {skus.map(s => (
                                    <option key={s.sku_code} value={s.sku_code}>
                                        {s.display_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs mb-1 text-gray-400">Dataset</label>
                            <select
                                value={datasetId}
                                onChange={e => setDatasetId(e.target.value)}
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                            >
                                <option value="">-- Select dataset --</option>
                                {datasets.map(d => (
                                    <option key={d.id} value={d.id}>
                                        {d.basin}/{d.region} {d.acquisition_date} ({d.slug})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs mb-1 text-gray-400">Scope</label>
                            <select
                                value={scope}
                                onChange={e => setScope(e.target.value)}
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                            >
                                <option value="full">Full mosaic</option>
                                <option value="aoi">AOI-scoped</option>
                            </select>
                        </div>

                        {scope === "aoi" && (
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">Coverage AOI</label>
                                <select
                                    value={coverageAoiId}
                                    onChange={e => setCoverageAoiId(e.target.value)}
                                    className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                                >
                                    <option value="">-- Select AOI --</option>
                                    {aois.map(a => (
                                        <option key={a.id} value={a.id}>
                                            {a.name} {a.basin && `(${a.basin}/${a.region})`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div>
                            <label className="block text-xs mb-1 text-gray-400">
                                Sq Mi Purchased
                            </label>
                            <input
                                type="number"
                                min="1"
                                step="any"
                                value={sqMi}
                                onChange={e => setSqMi(e.target.value)}
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">Term Start</label>
                                <input
                                    type="date"
                                    value={termStart}
                                    onChange={e => setTermStart(e.target.value)}
                                    className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">
                                    Term End <span className="text-fg-secondary">(optional)</span>
                                </label>
                                <input
                                    type="date"
                                    value={termEnd}
                                    onChange={e => setTermEnd(e.target.value)}
                                    className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs mb-1 text-gray-400">
                                Contract Ref <span className="text-fg-secondary">(optional)</span>
                            </label>
                            <input
                                value={contractRef}
                                onChange={e => setContractRef(e.target.value)}
                                placeholder="ACME-2026-001"
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                            />
                        </div>

                        {error && <div className="text-xs text-red-400">{error}</div>}

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={onClose}
                                className="px-3 py-1 rounded border border-bg-border text-gray-400 text-xs"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submit}
                                disabled={submitting || !datasetId || !skuCode}
                                className="
                  px-3 py-1 rounded text-xs
                  bg-accent-action/20 border border-accent-action/60
                  text-accent-action
                  disabled:opacity-50
                "
                            >
                                {submitting ? "Granting…" : "Grant"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────
// EditEntitlementModal — edit existing entitlement (with backend guards)
// ──────────────────────────────────────────────────────────
function EditEntitlementModal({ entitlement, onClose, onSuccess }) {
    const [sqMi, setSqMi] = useState(entitlement.sq_mi_purchased);
    const [termEnd, setTermEnd] = useState(
        entitlement.term_end ? entitlement.term_end.slice(0, 10) : ""
    );
    const [contractRef, setContractRef] = useState(entitlement.contract_ref || "");
    const [status, setStatus] = useState(entitlement.status);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    async function submit() {
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetchWithAuth(
                `/api/v1/platform/entitlements/${entitlement.id}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        sq_mi_purchased: Number(sqMi),
                        term_end: termEnd || null,
                        contract_ref: contractRef,
                        status,
                    }),
                }
            );
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err?.error || "Update failed");
            }
            await onSuccess();
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
            <div className="w-[440px] bg-bg-soft border border-bg-border rounded p-4 space-y-4">
                <h2 className="text-sm font-semibold">Edit Entitlement</h2>

                <div className="text-xs text-fg-secondary italic">
                    {entitlement.sku_display_name} · {entitlement.dataset_basin}/{entitlement.dataset_region} {entitlement.dataset_acquisition_date}
                    <br />
                    Consumed: {Number(entitlement.sq_mi_consumed).toFixed(1)} sq mi
                </div>

                <div>
                    <label className="block text-xs mb-1 text-gray-400">Sq Mi Purchased</label>
                    <input
                        type="number"
                        min="1"
                        step="any"
                        value={sqMi}
                        onChange={e => setSqMi(e.target.value)}
                        className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                    />
                    <p className="text-xs text-fg-secondary mt-1">
                        Must be ≥ {Number(entitlement.sq_mi_consumed).toFixed(1)} (current consumption).
                    </p>
                </div>

                <div>
                    <label className="block text-xs mb-1 text-gray-400">Term End</label>
                    <input
                        type="date"
                        value={termEnd}
                        onChange={e => setTermEnd(e.target.value)}
                        className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                    />
                </div>

                <div>
                    <label className="block text-xs mb-1 text-gray-400">Contract Ref</label>
                    <input
                        value={contractRef}
                        onChange={e => setContractRef(e.target.value)}
                        className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                    />
                </div>

                <div>
                    <label className="block text-xs mb-1 text-gray-400">Status</label>
                    <select
                        value={status}
                        onChange={e => setStatus(e.target.value)}
                        className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                    >
                        <option value="active">active</option>
                        <option value="suspended">suspended</option>
                        <option value="revoked">revoked</option>
                        <option value="expired">expired</option>
                    </select>
                </div>

                {error && <div className="text-xs text-red-400">{error}</div>}

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        onClick={onClose}
                        className="px-3 py-1 rounded border border-bg-border text-gray-400 text-xs"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={submitting}
                        className="
              px-3 py-1 rounded text-xs
              bg-accent-action/20 border border-accent-action/60
              text-accent-action
            "
                    >
                        {submitting ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}